import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useLocation } from 'react-router'

import {
  flattenPages,
  getNextPageParam,
  useInfiniteQuery,
} from '@fv/client-core'
import type { ListShipment, PagedShipments } from '@fv/client-types'

import { defaultPageSize } from '@/constants'
import { shipperFetch } from '@/utils/shipperFetch'
import { normalizeQueryString } from '@/utils/stringTransforms'

import { defaultSortBy, defaultSortDirection } from './useShipmentListParams'
import { useConfirmedSortDirectionPreference } from './useSortDirectionPreference'

export const confirmedShipmentKeys = {
  all: ['shipments', 'confirmed'] as const,
  filtered: (qs: string) =>
    [...confirmedShipmentKeys.all, normalizeQueryString(qs, ['load'])] as const,
}

export function isDefaultFilter(qs: string) {
  const params = new URLSearchParams(qs)
  const view = params.get('view')
  return !view || view === 'active'
}

export function useConfirmedShipments() {
  const [preferredSortDirection] = useConfirmedSortDirectionPreference()
  const { search } = useLocation()
  const params = new URLSearchParams(search)

  if (isDefaultFilter(search)) params.set('isArchived', 'false')
  if (!params.get('sortBy')) params.set('sortBy', defaultSortBy)
  if (!params.get('sortDirection')) {
    params.set(
      'sortDirection',
      (preferredSortDirection ?? defaultSortDirection).toString(),
    )
  }

  const shipmentsQuery = useInfiniteQuery({
    queryKey: confirmedShipmentKeys.filtered(params.toString()),
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      params.set('limit', String(defaultPageSize))
      params.set('skip', String(pageParam))
      return shipperFetch<PagedShipments>(
        `/shipments/ready-completed?${params.toString()}`,
      )
    },
    getNextPageParam,
  })

  // Handle invalid filters
  useEffect(() => {
    if (
      shipmentsQuery.error instanceof Error &&
      shipmentsQuery.error.name === 'EventValidationError'
    ) {
      toast.error(shipmentsQuery.error.message)
    }
  }, [shipmentsQuery.error])

  return shipmentsQuery
}

export const useConfirmedShipmentsFlat = () => {
  const shipmentsQuery = useConfirmedShipments()
  return flattenPages(shipmentsQuery.data?.pages)
}

export const useActiveShipmentSelection = (shipments: ListShipment[]) => {
  const [selected, setSelected] = useState<string[]>([])
  const handleCheckAll = () => {
    if (selected.length) {
      setSelected([])
      return
    }
    setSelected(shipments.map(s => s.loadId))
  }
  return {
    selected,
    setSelected,
    handleCheckAll,
    showBulkActions: !!selected.length,
  }
}
